import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { Product } from 'entities/product';
import ProductCard from 'components/library/ProductCard-V2/product-card';
import { ProductsGrid } from '../../styles';
import {
  PRODUCT_CONFIG,
  NEW_PRODUCT_SKUS,
  DAILY_ESSENTIALS_DUO,
} from '../../config/product-data';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import useClientSideMediaQuery from '../../../../../hooks/use-client-side-media-query';
import LoadingSpinner from 'components/reusable/spinners/loading-spinner';
import {
  createAddToCartAction,
  createViewDetailsAction,
} from 'lib/product-actions';
import { useWaitlistButtonText } from 'hooks/use-waitlist-button-text';
import { useContext } from 'react';
import { CartContext } from 'context-providers/cart-provider';
import { ProductsContext } from 'context-providers/products-provider';
import { applyShopifyPercentDiscount } from 'lib/utils/shopify-percent-discount';
import { BUNDLE_DISCOUNT_PERCENT } from 'lib/constants/bundle-discount';
import {
  applyPromoCodeDiscount,
  getPromoSavingsLabel,
} from 'lib/utils/coupons/apply-promo-code-discount';
import { discountAppliesToProduct } from 'lib/utils/coupons/discount-applies-to-product';

const WAITLIST_DIGIOH_CTA_ID = 'digioh-vs-out-of-stock-cta-button';

interface ProductGridProps {
  products: Product[];
  onProductClick?: (product: Product) => void;
  isVsOutOfStock?: boolean;
}

const ProductGrid: FC<ProductGridProps> = ({
  products,
  isVsOutOfStock = false,
}) => {
  const router = useRouter();
  const { openCartFromUrl, promoCodeDiscountInfo } = useContext(CartContext);
  const { currencyCode, getProductPrice, getProduct } =
    useContext(ProductsContext);
  const [isLoading, setIsLoading] = useState(false);

  // Localized amounts for the duo's components — the duo has no Shopify
  // variant of its own.
  const duoComponentPrices = DAILY_ESSENTIALS_DUO.componentProducts.map((sku) =>
    getProductPrice(sku),
  );
  const hasDuoComponentPrices = duoComponentPrices.every(Boolean);

  // USD shows the canonical plans-API price; other markets show the localized
  // variant amount (ds-duo: the sum of its components) or no price at all.
  const getDisplayPrice = (product: Product): string => {
    if (currencyCode === 'USD') {
      return product.price || '';
    }
    if (product.productSku === 'ds-duo') {
      if (!hasDuoComponentPrices) {
        return '';
      }
      return duoComponentPrices
        .reduce((sum, amount) => sum + Number(amount), 0)
        .toFixed(2);
    }
    return getProductPrice(product.productSku) ?? '';
  };

  // BUNDLE25 discounts each duo component line separately, so the sale price
  // is the sum of the individually discounted components.
  const duoSalePrice = hasDuoComponentPrices
    ? duoComponentPrices
        .reduce(
          (sum, amount) =>
            sum +
            Number(
              applyShopifyPercentDiscount(amount, BUNDLE_DISCOUNT_PERCENT),
            ),
          0,
        )
        .toFixed(2)
    : null;

  // The applied code's price for a product it actually covers. The duo is
  // excluded — it already shows the bundle price, and whether a code stacks on
  // top of that is decided by Shopify at checkout.
  const getPromoSalePrice = (
    product: Product,
    basePrice: string,
  ): string | null => {
    if (!promoCodeDiscountInfo || !basePrice) return null;

    const productId = getProduct(product.productSku)?.shopifyData?.productID;
    if (!discountAppliesToProduct(productId, promoCodeDiscountInfo.items)) {
      return null;
    }

    const { price: discounted } = applyPromoCodeDiscount(
      promoCodeDiscountInfo,
      Number(basePrice),
      currencyCode,
    );
    return discounted < Number(basePrice) ? discounted.toFixed(2) : null;
  };

  const waitlistButtonText = useWaitlistButtonText(isVsOutOfStock);

  const isSingleColumn = useClientSideMediaQuery(1024);
  const gridProducts = products.filter((product) => !product.isFeatured);

  if (gridProducts.length === 0) {
    return <p>No additional products found</p>;
  }

  // Products are already sorted by order property from the page level
  return (
    <>
      {isLoading && <LoadingSpinner />}
      <ProductsGrid>
        {gridProducts.map((product, index) => {
          const isNewProduct = NEW_PRODUCT_SKUS.includes(
            product.productSku as any,
          );
          const showDiscountPill = product.showDiscountPill ?? false;
          const discountText = product.discountText ?? 'Sale';
          const displayPrice = getDisplayPrice(product);
          const isDuo = product.productSku === 'ds-duo';
          const promoSalePrice = isDuo
            ? null
            : getPromoSalePrice(product, displayPrice);
          const salePrice = isDuo ? duoSalePrice : promoSalePrice;
          // A discounted card trades its "New" flag for the savings badge —
          // driven by the applied code, so it outlives the sale window.
          const newPillText =
            (promoSalePrice &&
              getPromoSavingsLabel(promoCodeDiscountInfo, currencyCode)) ||
            'New';
          const viewDetailsAction = createViewDetailsAction(
            product.productSku || 'SKU',
            router,
            { setIsLoading, isLoading },
          );
          const addToCartAction = createAddToCartAction(
            product.productSku || 'SKU',
            openCartFromUrl,
          );
          const isVsSelectedAndOutOfStock =
            isVsOutOfStock &&
            product.productSku === PRODUCT_SKUS.VAGINAL_SYNBIOTIC;
          const waitlistAction = {
            label: waitlistButtonText,
            id: WAITLIST_DIGIOH_CTA_ID,
            disabled: waitlistButtonText === 'Waitlist Joined',
            onClick: () => {}, // Empty onClick to prevent button from being auto-disabled
          };
          const primaryAction = isVsSelectedAndOutOfStock
            ? waitlistAction
            : viewDetailsAction;
          const secondaryAction = isVsSelectedAndOutOfStock
            ? null
            : addToCartAction;

          return (
            <ProductCard
              key={product.id || index + 1}
              className='grid-product'
              data-testid={`grid-product-card-${product.productSku}`}
              showNewPill={isNewProduct}
              newPillText={newPillText}
              newPillColor='seedgreen'
              newPillFontColor='snowwhite'
              showDiscountPill={showDiscountPill}
              showCapsuleImage={false}
              capsuleImageUrl=''
              primaryAction={primaryAction}
              secondaryAction={secondaryAction}
              imageClickAction={
                isVsSelectedAndOutOfStock
                  ? undefined
                  : () => viewDetailsAction.onClick?.()
              }
              productData={{
                productName:
                  PRODUCT_CONFIG[product.productSku]?.displayName || 'Product',
                productSku: product.productSku || 'SKU',
                productDescription: isSingleColumn
                  ? PRODUCT_CONFIG[product.productSku]?.mobileDescription ||
                    PRODUCT_CONFIG[product.productSku]?.description ||
                    'Product description'
                  : PRODUCT_CONFIG[product.productSku]?.description ||
                    'Product description',
                productAlphaCode:
                  product.productSku === 'ds-duo'
                    ? ''
                    : PRODUCT_CONFIG[product.productSku]?.alphaCode || '',
                productAlphaCodeTrademark:
                  PRODUCT_CONFIG[product.productSku]?.alphaCodeTrademark,
                productAlphaCodeRegistered:
                  PRODUCT_CONFIG[product.productSku]?.alphaCodeRegistered,
                productImageUrl: product.image || '',
                price: displayPrice,
                salePrice,
                discountText,
                pillColor: PRODUCT_CONFIG[product.productSku]?.pillColor,
                pillPosition:
                  PRODUCT_CONFIG[product.productSku]?.pillPosition || 'left',
              }}
              imageConfig={{
                flexGrow: 1,
                flexShrink: 0,
                flexBasis: '35%',
                ...product.imageConfig,
              }}
              contentConfig={{
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: '60%',
                padding: 'var(--spacing-16) 0',
                actionConfig: {},
              }}
              styleConfig={{
                contentPosition: 'left',
                mobileLayout: 'horizontal',
                backgroundColor: 'var(--fill-secondary)',
              }}
            />
          );
        })}
      </ProductsGrid>
    </>
  );
};

export default ProductGrid;
